import { create } from 'zustand'
import {
  dropSession,
  getActiveSlug,
  setActiveSlug,
  storeSession,
} from '../lib/api/client'
import {
  clearServiceWorkerCaches,
  readSession,
  type SessionUser,
  type StoredSession,
} from '../lib/api/session-storage'
import {
  applyTheme,
  cacheBranding,
  readCachedBranding,
  readPreferredMode,
  storePreferredMode,
  type Branding,
  type ThemeIntensity,
  type ThemeMode,
} from '../lib/theme/theme'

interface SessionState {
  user: SessionUser | null
  branding: Branding | null
  mode: ThemeMode
  intensity: ThemeIntensity
  slug: string | undefined
  status: 'loading' | 'authenticated' | 'anonymous'

  bootstrap: (slug: string | undefined, intensity: ThemeIntensity) => void
  signIn: (session: StoredSession) => void
  signOut: () => Promise<void>
  setUser: (user: SessionUser) => void
  setBranding: (branding: Branding) => void
  hydrateTenant: (branding: Branding, slug: string) => void
  setMode: (mode: ThemeMode) => void
  toggleMode: () => void
}

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  branding: null,
  mode: 'light',
  intensity: 'panel',
  slug: undefined,
  status: 'loading',

  /** Define o escopo da sessão (painel ou portal de um slug) e restaura o que houver. */
  bootstrap: (slug, intensity) => {
    setActiveSlug(slug)
    const stored = readSession(slug)

    /**
     * A marca do CACHE vem antes da marca da sessão.
     *
     * `stored.user.branding` é um retrato do momento do login. Trocar o logo
     * ou a cor e recarregar a página trazia de volta a identidade antiga —
     * no produto cujo diferencial é justamente ser white-label. O cache é
     * reescrito a cada alteração, então é ele que está atualizado.
     *
     * Só vale quando existe sessão: sem isso, a marca do personal anterior
     * apareceria para quem abrisse a tela de login depois dele.
     */
    const branding = stored
      ? (readCachedBranding(slug) ?? stored.user.branding)
      : get().branding

    set({
      slug,
      intensity,
      user: stored?.user ?? null,
      branding,
      status: stored ? 'authenticated' : 'anonymous',
    })

    if (stored && branding) {
      const mode = readPreferredMode(slug, branding.darkMode ? 'dark' : 'light')
      set({ mode })
      applyTheme(branding, mode, intensity)
    }
  },

  signIn: (session) => {
    storeSession(session)
    cacheBranding(session.user.branding, getActiveSlug())

    const mode = readPreferredMode(
      getActiveSlug(),
      session.user.branding.darkMode ? 'dark' : 'light',
    )

    set({
      user: session.user,
      branding: session.user.branding,
      mode,
      status: 'authenticated',
    })

    applyTheme(session.user.branding, mode, get().intensity)
  },

  signOut: async () => {
    const slug = getActiveSlug()
    dropSession()
    // O treino do aluno anterior não pode continuar legível no aparelho.
    if (slug) await clearServiceWorkerCaches(slug)
    set({ user: null, status: 'anonymous' })
  },

  setUser: (user) => set({ user }),

  /**
   * Marca pública de um slug, antes de existir sessão. Resolve o modo na
   * ordem certa: preferência explícita do aluno > tema padrão do personal.
   * Sem isto, um personal que escolheu tema escuro entregava a tela de
   * login clara para quem ainda não logou.
   */
  hydrateTenant: (branding, slug) => {
    const mode = readPreferredMode(slug, branding.darkMode ? 'dark' : 'light')
    set({ branding, mode, slug, intensity: 'full' })
    cacheBranding(branding, slug)
    applyTheme(branding, mode, 'full')
  },

  setBranding: (branding) => {
    const { mode, intensity, slug } = get()
    set({ branding })
    cacheBranding(branding, slug)
    applyTheme(branding, mode, intensity)
  },

  setMode: (mode) => {
    const { branding, intensity, slug } = get()
    set({ mode })
    storePreferredMode(mode, slug)
    if (branding) applyTheme(branding, mode, intensity)
  },

  toggleMode: () => {
    get().setMode(get().mode === 'dark' ? 'light' : 'dark')
  },
}))
